import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import {
  MoreVertical, Edit, Pause, Play, Trash2, XCircle, CheckCircle2,
  MapPin, Calendar, Clock, Users, Fish, ArrowRight,
} from "lucide-react";
import { api, formatError } from "../lib/api";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter,
  AlertDialogCancel, AlertDialogAction,
} from "./ui/alert-dialog";
import EditTripDialog from "./EditTripDialog";

const STATUS_PILLS = {
  active: { label: "Active", bg: "bg-turquoise/15 text-bretagne" },
  paused: { label: "En pause", bg: "bg-jaune/30 text-ocean" },
  complete: { label: "Complète", bg: "bg-ocean/10 text-ocean" },
  cancelled: { label: "Annulée", bg: "bg-corail/15 text-corail" },
  expired: { label: "Terminée", bg: "bg-ocean/5 text-ocean/60" },
};

function fmt(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" })
      + " · " + d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export default function CaptainTripCard({ trip }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [confirm, setConfirm] = useState(null); // 'delete' | 'cancel' | null
  const eff = trip.effective_status || trip.status || "active";
  const pill = STATUS_PILLS[eff] || STATUS_PILLS.active;
  const isPast = eff === "expired";
  const isCancelled = eff === "cancelled";

  const refresh = () => qc.invalidateQueries({ queryKey: ["my-trips"] });

  const changeStatus = async (status, msg) => {
    try {
      await api.post(`/trips/${trip.trip_id}/status`, { status });
      toast.success(msg);
      refresh();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/trips/${trip.trip_id}`);
      toast.success("Annonce supprimée");
      refresh();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); }
    setConfirm(null);
  };

  return (
    <>
      <div
        data-testid={`captain-trip-${trip.trip_id}`}
        className="bg-blanc border border-brume rounded-2xl p-5 relative"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-bretagne">
                <Fish className="w-3.5 h-3.5" /> {trip.type}
              </span>
              <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${pill.bg}`}>
                {pill.label}
              </span>
            </div>
            <h3 className="mt-1 font-display text-base md:text-lg font-bold text-ocean leading-snug">
              {trip.title}
            </h3>
          </div>

          {/* ── Actions dropdown ── */}
          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid={`captain-trip-menu-${trip.trip_id}`}
              className="w-9 h-9 rounded-full hover:bg-brume grid place-items-center text-ocean shrink-0"
              aria-label="Actions"
            >
              <MoreVertical className="w-5 h-5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-blanc border-brume w-56">
              <DropdownMenuItem
                data-testid={`action-edit-${trip.trip_id}`}
                onSelect={() => setEditing(true)}
                className="cursor-pointer"
                disabled={isPast || isCancelled}
              >
                <Edit className="w-4 h-4 mr-2" /> Modifier
              </DropdownMenuItem>
              {eff === "active" && (
                <DropdownMenuItem
                  data-testid={`action-pause-${trip.trip_id}`}
                  onSelect={() => changeStatus("paused", "Annonce mise en pause")}
                  className="cursor-pointer"
                >
                  <Pause className="w-4 h-4 mr-2" /> Mettre en pause
                </DropdownMenuItem>
              )}
              {eff === "paused" && (
                <DropdownMenuItem
                  data-testid={`action-resume-${trip.trip_id}`}
                  onSelect={() => changeStatus("active", "Annonce réactivée")}
                  className="cursor-pointer"
                >
                  <Play className="w-4 h-4 mr-2" /> Réactiver
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                data-testid={`action-complete-${trip.trip_id}`}
                onSelect={() => changeStatus("complete", "Sortie marquée comme complète")}
                className="cursor-pointer"
                disabled={isPast || isCancelled || eff === "complete"}
              >
                <CheckCircle2 className="w-4 h-4 mr-2" /> Toutes les places sont prises
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid={`action-cancel-${trip.trip_id}`}
                onSelect={() => setConfirm("cancel")}
                className="cursor-pointer text-corail focus:text-corail"
                disabled={isPast || isCancelled}
              >
                <XCircle className="w-4 h-4 mr-2" /> Annuler la sortie
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid={`action-delete-${trip.trip_id}`}
                onSelect={() => setConfirm("delete")}
                className="cursor-pointer text-corail focus:text-corail"
              >
                <Trash2 className="w-4 h-4 mr-2" /> Supprimer l'annonce
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-y-1.5 gap-x-3 text-sm text-ocean/85">
          <span className="inline-flex items-center gap-1.5">
            <MapPin className="w-4 h-4 text-bretagne shrink-0" /> {trip.port}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Calendar className="w-4 h-4 text-bretagne shrink-0" /> {fmt(trip.date)}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Clock className="w-4 h-4 text-bretagne shrink-0" /> {trip.duration_hours}h
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Users className="w-4 h-4 text-bretagne shrink-0" /> {trip.spots_left}/{trip.spots_total} places
          </span>
        </div>

        <div className="mt-4 pt-3 border-t border-brume flex items-center justify-between gap-3">
          <div className="text-sm">
            <span className="text-[10px] uppercase tracking-widest text-bretagne/80 font-bold">Participation</span>
            <span className="ml-1.5 font-display text-lg font-bold text-ocean">{trip.price_per_person}€</span>
          </div>
          <Link
            to={`/sortie/${trip.trip_id}`}
            className="text-xs font-bold text-ocean hover:underline inline-flex items-center gap-1"
          >
            Voir l'annonce <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>

      {/* Edit dialog */}
      {editing && (
        <EditTripDialog
          trip={trip}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); refresh(); }}
        />
      )}

      {/* Confirm cancel */}
      <AlertDialog open={confirm === "cancel"} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Annuler la sortie&nbsp;?</AlertDialogTitle>
            <AlertDialogDescription>
              Les participants seront notifiés de l'annulation. L'annonce passera en statut "Annulée".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel-no">Non</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-cancel-yes"
              onClick={() => { changeStatus("cancelled", "Sortie annulée — participants notifiés"); setConfirm(null); }}
              className="bg-corail hover:bg-corail/90 text-blanc"
            >
              Oui, annuler la sortie
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm delete */}
      <AlertDialog open={confirm === "delete"} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Voulez-vous vraiment supprimer cette annonce&nbsp;?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est <strong>immédiate et définitive</strong>. Les réservations associées seront également supprimées.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-delete-no">Non</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-yes"
              onClick={doDelete}
              className="bg-corail hover:bg-corail/90 text-blanc"
            >
              Oui, supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
